import { useEffect, useRef, useState } from 'react';
import { createLocalVideoTrack, type LocalVideoTrack } from 'livekit-client';
import { useGameStore } from '@campus/core';
import { useDeviceEnumeration, deviceOptions, SUPPORTS_AUDIO_OUTPUT_SELECTION } from '../hooks/useDeviceEnumeration.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { askNotificationPermissionOnce } from '../../lib/mediaReminder.js';
import { syncTrackBackground } from '../../services/videoBackground.js';
import { onCustomBackgroundChange } from '../../lib/videoBackgrounds.js';
import { VideoBackgroundPicker } from '../components/VideoBackgroundPicker.js';
import { Button, ControlBar, ControlButton, Icon, Select } from '../ds/index.js';

/**
 * LobbyPage — ecran de pre-connexion (Zoom/Meet/Gather) entre AvatarPage et
 * le campus. Affiche si authState === 'lobby'. Choix du micro/camera/haut-
 * parleur + preview camera avant d'entrer.
 *
 * La preview camera cree un LocalVideoTrack independant (createLocalVideoTrack),
 * jamais publie sur une Room — la vraie connexion LiveKit (zoneRoom) ne
 * demarre qu'apres authState -> 'in-game' (voir GameBridge.ts, zone:entered).
 * Track arrete et detache au demontage / desactivation, jamais laisse en vol.
 */
export function LobbyPage() {
  const settings = useGameStore((s) => s.settings);
  const updateSettings = useGameStore((s) => s.updateSettings);
  const setAuthState = useGameStore((s) => s.setAuthState);
  const displayName = useGameStore((s) => s.displayName);
  const isMobile = useIsMobile();

  const [micOn, setMicOn] = useState(settings.audioEnabled);
  const [camOn, setCamOn] = useState(settings.videoEnabled);
  const [camError, setCamError] = useState<string | null>(null);

  const videoContainerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<LocalVideoTrack | null>(null);

  const devices = useDeviceEnumeration();

  useEffect(() => {
    if (!camOn) return;
    let cancelled = false;
    setCamError(null);

    createLocalVideoTrack(settings.selectedCameraId ? { deviceId: settings.selectedCameraId } : undefined)
      .then((track) => {
        if (cancelled) { track.stop(); return; }
        trackRef.current = track;
        const el = track.attach();
        el.style.width = '100%';
        el.style.height = '100%';
        el.style.objectFit = 'cover';
        el.style.transform = 'scaleX(-1)'; // effet miroir — convention preview self-camera
        videoContainerRef.current?.appendChild(el);
        void syncTrackBackground(track);
      })
      .catch(() => {
        if (cancelled) return;
        setCamError("Impossible d'accéder à la caméra. Vérifie l'autorisation dans ton navigateur.");
        setCamOn(false);
      });

    return () => {
      cancelled = true;
      const track = trackRef.current;
      if (track) {
        track.detach().forEach((el) => el.remove());
        track.stop();
        trackRef.current = null;
      }
    };
  }, [camOn, settings.selectedCameraId]);

  // Arrière-plan changé ici : l'aperçu le montre tout de suite.
  useEffect(() => {
    if (trackRef.current) void syncTrackBackground(trackRef.current);
  }, [settings.videoBackground]);
  useEffect(() => onCustomBackgroundChange(() => {
    if (trackRef.current) void syncTrackBackground(trackRef.current);
  }), []);

  function handleJoin() {
    updateSettings({ audioEnabled: micOn, videoEnabled: camOn });
    // Micro ou caméra activés : rappel possible plus tard (lib/mediaReminder),
    // l'autorisation des notifications ne se demande que dans un clic.
    if (micOn || camOn) askNotificationPermissionOnce();
    setAuthState('in-game');
  }

  const name = displayName?.trim();
  const initial = (name?.[0] ?? '?').toUpperCase();

  return (
    <div
      className="pl-page"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // 100dvh : sur iPhone, 100vh inclut la zone sous la barre de Safari,
        // qui cachait le bouton « Entrer dans le campus ».
        width: '100vw', height: '100dvh', boxSizing: 'border-box',
        padding: 'var(--space-4)',
        paddingBottom: 'calc(var(--space-4) + env(safe-area-inset-bottom, 0px))',
        overflowY: 'auto',
      }}
    >
      <main
        style={{
          width: '100%', maxWidth: 760, boxSizing: 'border-box',
          padding: isMobile ? 'var(--space-6) var(--space-4)' : 'var(--space-8)',
          background: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex', flexDirection: 'column', gap: 'var(--space-6)',
          margin: 'auto 0',
        }}
      >
        <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <h1 style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-extrabold)' as unknown as number,
            fontSize: isMobile ? 'var(--text-xl)' : 'var(--text-2xl)',
            lineHeight: 'var(--leading-tight)',
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--color-text-primary)',
          }}>
            {name ? `Salut ${name}, on\u00a0y\u00a0va\u00a0?` : 'On\u00a0y\u00a0va\u00a0?'}
          </h1>
          <p style={{ margin: 0, fontSize: 'var(--text-base)', lineHeight: 'var(--leading-normal)', color: 'var(--color-text-secondary)' }}>
            Vérifie ton micro et ta caméra avant d'entrer.
          </p>
        </header>

        <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
          {/* Aperçu caméra + micro/caméra */}
          <section aria-label="Aperçu" style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div
              style={{
                position: 'relative', width: '100%', aspectRatio: '16 / 9',
                borderRadius: 'var(--radius-lg)', overflow: 'hidden',
                background: 'var(--color-bg-raised)', border: '1px solid var(--color-border-default)',
              }}
            >
              <div ref={videoContainerRef} style={{ position: 'absolute', inset: 0 }} />
              {!camOn && (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 'var(--space-3)',
                }}>
                  <div aria-hidden="true" style={{
                    width: 64, height: 64, borderRadius: 'var(--radius-full)',
                    background: 'var(--color-primary)', color: 'var(--color-on-primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 28,
                  }}>
                    {initial}
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-label)', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                    <Icon name="video-off" size={14} />
                    Caméra coupée
                  </span>
                </div>
              )}
            </div>
            {camError && (
              <p role="alert" style={{ margin: 0, fontSize: 'var(--text-label)', lineHeight: 1.45, color: 'var(--color-danger)', textAlign: 'center' }}>
                {camError}
              </p>
            )}
            <ControlBar label="Micro et caméra">
              <ControlButton icon={micOn ? 'mic' : 'mic-off'} label="Micro" off={!micOn} onClick={() => setMicOn((v) => !v)} />
              <ControlButton icon={camOn ? 'video' : 'video-off'} label="Caméra" off={!camOn} onClick={() => setCamOn((v) => !v)} />
            </ControlBar>
          </section>

          {/* Choix des périphériques */}
          <section aria-label="Périphériques" style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Select
              label="Microphone"
              value={settings.selectedMicId}
              options={deviceOptions(devices, 'audioinput', 'Microphone')}
              onChange={(e) => updateSettings({ selectedMicId: e.target.value })}
            />
            <Select
              label="Caméra"
              value={settings.selectedCameraId}
              options={deviceOptions(devices, 'videoinput', 'Caméra')}
              onChange={(e) => updateSettings({ selectedCameraId: e.target.value })}
            />
            <VideoBackgroundPicker compact />
            {SUPPORTS_AUDIO_OUTPUT_SELECTION && (
              <Select
                label="Haut-parleur"
                value={settings.selectedSpeakerId}
                options={deviceOptions(devices, 'audiooutput', 'Haut-parleur')}
                onChange={(e) => updateSettings({ selectedSpeakerId: e.target.value })}
              />
            )}
            <p style={{ margin: 0, fontSize: 'var(--text-xs)', lineHeight: 1.35, color: 'var(--color-text-muted)' }}>
              Tu pourras les changer à tout moment dans les Réglages.
            </p>
          </section>
        </div>

        <footer style={{
          display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end',
          paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-border-default)',
          // Mobile : le bouton reste visible en bas même si l'aperçu caméra déborde.
          ...(isMobile ? {
            position: 'sticky', bottom: 0, background: 'var(--color-bg-surface)', paddingBottom: 'var(--space-2)',
          } : {}),
        }}>
          <Button variant="primary" onClick={handleJoin} style={isMobile ? { width: '100%', height: 44 } : undefined}>
            Entrer dans le campus
          </Button>
        </footer>
      </main>
    </div>
  );
}
